// Cluster loose stub SKUs into Master Products (Screen 1) API (admin, Ring 3).
//
// Thin wrappers over the live backend grouping endpoints:
//   GET  /admin/inventory/grouping/clusters?limit=500  (suggest system clusters)
//   POST /admin/inventory/grouping/create-from-cluster  (create master + link)
//   GET  /admin/inventory/grouping/count                (nav badge)
// In maket mode the same fetch(apiUrl(...)) calls are intercepted by MSW.

import { apiUrl } from '../lib/api'
import { readErrorMessage } from '../lib/api-problem'

export type ClusterMember = {
  master_catalog_id: string
  sku_id: string
  sku_code: string
  title: string
}

export type GroupingCluster = {
  suggested_name: string
  suggested_code: string
  members: ClusterMember[]
  confidence: 'high' | 'medium'
  reason: string
  // When set, the suggested_code already matches an alive product: "Create &
  // link" merges into it instead of creating a new Master Product.
  existing_master_id?: string | null
  existing_master_name?: string | null
}

export type SuggestClustersResult = {
  clusters: GroupingCluster[]
  ungrouped: ClusterMember[]
  stub_count: number
}

export type CreateFromClusterRequest = {
  name: string
  code: string
  member_master_catalog_ids: string[]
  reason: string
}

export type CreateFromClusterResult = {
  master_catalog_id: string
  linked_skus: number
}

export type GroupingCountResult = {
  stub_count: number
}

export async function suggestClusters(
  limit = 500,
): Promise<SuggestClustersResult> {
  const res = await fetch(
    apiUrl(`/admin/inventory/grouping/clusters?limit=${limit}`),
  )
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as SuggestClustersResult
}

export async function createFromCluster(
  payload: CreateFromClusterRequest,
): Promise<CreateFromClusterResult> {
  const res = await fetch(apiUrl('/admin/inventory/grouping/create-from-cluster'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as CreateFromClusterResult
}

export async function groupingCount(): Promise<GroupingCountResult> {
  const res = await fetch(apiUrl('/admin/inventory/grouping/count'))
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as GroupingCountResult
}

export type ConfirmStandaloneResult = {
  master_catalog_id: string
}

// Acknowledge a lone stub as a legitimate standalone product: it stays a normal
// catalog product but leaves the Ungrouped worklist + nav count.
export async function confirmStandalone(
  masterCatalogId: string,
): Promise<ConfirmStandaloneResult> {
  const res = await fetch(
    apiUrl('/admin/inventory/grouping/confirm-standalone'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ master_catalog_id: masterCatalogId }),
    },
  )
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return (await res.json()) as ConfirmStandaloneResult
}
